import { ApiResponse } from './types';
import { apiClient } from './core/apiClient';
import { getPatients } from './patientService';
import {
  ApiAppointmentStatus,
  IAppointment,
  IAppointmentAvailabilitySlot,
  IBulkCancelAppointmentsRequest,
  IBulkCancelResult,
  ICreateAppointmentTypeRequest,
  IAppointmentFiltersDTO,
  IAppointmentType,
  IWaitingListEntry,
  IUpdateAppointmentTypeRequest,
} from '../types/interfaces/IAppointment';
import { IPatient } from '../types/interfaces/IPatient';
import {
  normalizeAppointmentStatus,
  toApiAppointmentStatus,
} from './adapters/appointmentStatusAdapter';

interface AppointmentApiDto {
  id: string;
  patientId: string;
  userId: string;
  institutionId: string;
  specialtyId: string;
  appointmentTypeId: string;
  status: ApiAppointmentStatus;
  appointmentMode: NonNullable<IAppointment['appointmentMode']>;
  isOverbooked: boolean;
  isUrgent: boolean;
  consultationReason: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart?: string;
  actualEnd?: string;
  waitingRoomCheckinAt?: string;
  virtualMeetingUrl?: string;
  cancelledAt?: string;
  cancelledByUserId?: string;
  cancellationReason?: string;
  rescheduledFromId?: string;
  recurrenceGroupId?: string;
  recurrenceIndex?: number;
  reminderSentAt?: string;
  notes?: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

type PatientLookup = Map<string, IPatient>;
type AppointmentTypeLookup = Map<string, IAppointmentType>;

function buildAppointmentQuery(filters: IAppointmentFiltersDTO): Record<string, string> {
  const query: Record<string, string> = {
    institutionId: filters.institutionId,
  };

  if (filters.userId) {
    query.userId = filters.userId;
  }

  if (filters.dateFrom) {
    query.from = new Date(filters.dateFrom).toISOString();
  }

  if (filters.dateTo) {
    query.to = new Date(filters.dateTo).toISOString();
  }

  if (filters.status) {
    query.status = toApiAppointmentStatus(filters.status) || filters.status;
  }

  return query;
}

async function getPatientLookup(institutionId: string): Promise<PatientLookup> {
  if (!institutionId) {
    return new Map();
  }

  const response = await getPatients(institutionId);
  if (!response.success || !response.data) {
    return new Map();
  }

  return new Map(response.data.map(patient => [patient.id, patient]));
}

async function getAppointmentTypeLookup(
  institutionId: string,
  specialtyId?: string
): Promise<AppointmentTypeLookup> {
  if (!institutionId) {
    return new Map();
  }

  const params: Record<string, string> = { institutionId };
  if (specialtyId) {
    params.specialtyId = specialtyId;
  }

  const response = await apiClient.get<IAppointmentType[]>('/appointmenttypes', params);
  if (!response.success || !response.data) {
    return new Map();
  }

  return new Map(response.data.map(type => [type.id, type]));
}

function mapAppointmentDto(
  appointment: AppointmentApiDto,
  patientLookup: PatientLookup,
  appointmentTypeLookup: AppointmentTypeLookup,
  fallback?: Partial<IAppointment>
): IAppointment {
  const patient = patientLookup.get(appointment.patientId);
  const appointmentType = appointmentTypeLookup.get(appointment.appointmentTypeId);
  const start = new Date(appointment.scheduledStart);
  const end = new Date(appointment.scheduledEnd);
  const patientName = patient?.name || fallback?.patientName || 'Paciente';

  return {
    id: appointment.id,
    title: fallback?.title || `Turno con ${patientName}`,
    patientName,
    patientId: appointment.patientId,
    start,
    end,
    duration: Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000)),
    type: appointmentType?.name || fallback?.type || 'Consulta',
    appointmentTypeId: appointment.appointmentTypeId,
    notes: appointment.notes || appointment.consultationReason,
    consultationReason: appointment.consultationReason,
    status: normalizeAppointmentStatus(appointment.status),
    appointmentMode: appointment.appointmentMode,
    isOverbooked: appointment.isOverbooked,
    isUrgent: appointment.isUrgent,
    virtualMeetingUrl: appointment.virtualMeetingUrl,
    cancellationReason: appointment.cancellationReason,
    rescheduledFromId: appointment.rescheduledFromId,
    recurrenceGroupId: appointment.recurrenceGroupId,
    recurrenceIndex: appointment.recurrenceIndex,
    doctorId: appointment.userId,
    doctorName: fallback?.doctorName || '',
    institutionId: appointment.institutionId,
    specialtyId: appointment.specialtyId,
    createdAt: new Date(appointment.createdAt),
    updatedAt: new Date(appointment.updatedAt),
  };
}

async function enrichAppointments(
  appointments: AppointmentApiDto[],
  filters?: Pick<IAppointmentFiltersDTO, 'institutionId' | 'specialtyId'>,
  fallbackById?: Record<string, Partial<IAppointment>>
): Promise<IAppointment[]> {
  const institutionId = filters?.institutionId || appointments[0]?.institutionId || '';
  const [patientLookup, appointmentTypeLookup] = await Promise.all([
    getPatientLookup(institutionId),
    getAppointmentTypeLookup(
      institutionId,
      filters?.specialtyId || appointments[0]?.specialtyId
    ),
  ]);

  return appointments.map(appointment =>
    mapAppointmentDto(
      appointment,
      patientLookup,
      appointmentTypeLookup,
      fallbackById?.[appointment.id]
    )
  );
}

export async function getAppointments(
  filters: IAppointmentFiltersDTO
): Promise<ApiResponse<IAppointment[]>> {
  const response = await apiClient.get<AppointmentApiDto[]>(
    '/appointments',
    buildAppointmentQuery(filters)
  );

  if (!response.success || !response.data) {
    return {
      ...response,
      data: [],
    };
  }

  const data = await enrichAppointments(response.data, filters);
  return {
    ...response,
    data,
  };
}

export async function getAppointmentById(
  id: string
): Promise<ApiResponse<IAppointment>> {
  const response = await apiClient.get<AppointmentApiDto>(`/appointments/${id}`);
  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IAppointment,
    };
  }

  const [appointment] = await enrichAppointments([response.data], {
    institutionId: response.data.institutionId,
    specialtyId: response.data.specialtyId,
  });

  return {
    ...response,
    data: appointment,
  };
}

export async function getAppointmentAvailability(
  userId: string,
  institutionId: string,
  date: string
): Promise<ApiResponse<IAppointmentAvailabilitySlot[]>> {
  return apiClient.get<IAppointmentAvailabilitySlot[]>('/appointments/availability', {
    userId,
    institutionId,
    date,
  });
}

export async function createAppointment(
  data: Omit<IAppointment, 'id'>
): Promise<ApiResponse<IAppointment>> {
  const response = await apiClient.post<AppointmentApiDto>('/appointments', {
    patientId: data.patientId,
    userId: data.doctorId,
    institutionId: data.institutionId,
    specialtyId: data.specialtyId,
    appointmentTypeId: data.appointmentTypeId,
    appointmentMode: data.appointmentMode || 'inPerson',
    isOverbooked: data.isOverbooked || false,
    isUrgent: data.isUrgent || false,
    consultationReason: data.notes?.trim() || `Consulta con ${data.patientName}`,
    scheduledStart: data.start.toISOString(),
    scheduledEnd: data.end.toISOString(),
    recurrenceGroupId: data.recurrenceGroupId,
    recurrenceIndex: data.recurrenceIndex,
    virtualMeetingUrl: data.virtualMeetingUrl,
    notes: data.notes,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IAppointment,
    };
  }

  const [appointment] = await enrichAppointments(
    [response.data],
    {
      institutionId: data.institutionId,
      specialtyId: data.specialtyId,
    },
    {
      [response.data.id]: {
        patientName: data.patientName,
        doctorName: data.doctorName,
        title: data.title,
        type: data.type,
      },
    }
  );

  return {
    ...response,
    data: appointment,
  };
}

export async function updateAppointment(
  id: string,
  data: Partial<IAppointment>
): Promise<ApiResponse<IAppointment>> {
  const response = await apiClient.put<AppointmentApiDto>(
    `/appointments/${id}/reschedule`,
    {
      newScheduledStart: data.start?.toISOString(),
      newScheduledEnd: data.end?.toISOString(),
      isOverbooked: data.isOverbooked || false,
      isUrgent: data.isUrgent || false,
      appointmentMode: data.appointmentMode || 'inPerson',
      virtualMeetingUrl: data.virtualMeetingUrl,
      notes: data.notes,
    }
  );

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IAppointment,
    };
  }

  const [appointment] = await enrichAppointments(
    [response.data],
    {
      institutionId: response.data.institutionId,
      specialtyId: response.data.specialtyId,
    },
    {
      [response.data.id]: {
        patientName: data.patientName,
        doctorName: data.doctorName,
        title: data.title,
        type: data.type,
      },
    }
  );

  return {
    ...response,
    data: appointment,
  };
}

export async function updateAppointmentStatus(
  id: string,
  status: NonNullable<IAppointment['status']>,
  cancellationReason?: string
): Promise<ApiResponse<IAppointment>> {
  const response = await apiClient.put<AppointmentApiDto>(
    `/appointments/${id}/status`,
    {
      status: toApiAppointmentStatus(status),
      cancellationReason,
    }
  );

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IAppointment,
    };
  }

  const [appointment] = await enrichAppointments([
    response.data,
  ], {
    institutionId: response.data.institutionId,
    specialtyId: response.data.specialtyId,
  });

  return {
    ...response,
    data: appointment,
  };
}

export async function deleteAppointment(
  id: string,
  reason?: string
): Promise<ApiResponse<boolean>> {
  const endpoint = reason
    ? `/appointments/${id}?reason=${encodeURIComponent(reason)}`
    : `/appointments/${id}`;
  const response = await apiClient.delete<AppointmentApiDto>(endpoint);

  return {
    ...response,
    data: response.success,
  } as ApiResponse<boolean>;
}

export async function bulkCancelAppointments(
  payload: IBulkCancelAppointmentsRequest
): Promise<ApiResponse<IBulkCancelResult>> {
  return apiClient.post<IBulkCancelResult>('/appointments/bulk-cancel', payload);
}

export async function getAppointmentTypes(
  institutionId: string,
  specialtyId?: string
): Promise<ApiResponse<IAppointmentType[]>> {
  const params: Record<string, string> = { institutionId };
  if (specialtyId) {
    params.specialtyId = specialtyId;
  }

  return apiClient.get<IAppointmentType[]>('/appointmenttypes', params);
}

export async function createAppointmentType(
  payload: ICreateAppointmentTypeRequest
): Promise<ApiResponse<IAppointmentType>> {
  return apiClient.post<IAppointmentType>('/appointmenttypes', payload);
}

export async function updateAppointmentType(
  id: string,
  payload: IUpdateAppointmentTypeRequest
): Promise<ApiResponse<IAppointmentType>> {
  return apiClient.put<IAppointmentType>(`/appointmenttypes/${id}`, payload);
}

export async function getUpcomingAppointments(
  days: number = 7,
  filters: IAppointmentFiltersDTO
): Promise<ApiResponse<IAppointment[]>> {
  const now = new Date();
  const endDate = new Date(now);
  endDate.setDate(now.getDate() + days);

  return getAppointments({
    ...filters,
    dateFrom: now.toISOString(),
    dateTo: endDate.toISOString(),
  });
}

export async function getWaitingList(
  institutionId: string,
  specialtyId?: string,
  userId?: string,
  status?: IWaitingListEntry['status']
): Promise<ApiResponse<IWaitingListEntry[]>> {
  const params: Record<string, string> = { institutionId };

  if (specialtyId) {
    params.specialtyId = specialtyId;
  }

  if (userId) {
    params.userId = userId;
  }

  if (status) {
    params.status = status;
  }

  return apiClient.get<IWaitingListEntry[]>('/waiting-list', params);
}

export async function createWaitingListEntry(
  payload: Omit<IWaitingListEntry, 'id' | 'requestedAt' | 'patientName' | 'status'> & {
    notes?: string;
    priority: IWaitingListEntry['priority'];
    patientName?: string;
  }
): Promise<ApiResponse<IWaitingListEntry>> {
  return apiClient.post<IWaitingListEntry>('/waiting-list', {
    patientId: payload.patientId,
    institutionId: payload.institutionId,
    specialtyId: payload.specialtyId,
    userId: payload.userId,
    priority: payload.priority,
    notes: payload.notes,
  });
}

export async function updateWaitingListEntryStatus(
  id: string,
  status: IWaitingListEntry['status']
): Promise<ApiResponse<IWaitingListEntry>> {
  return apiClient.put<IWaitingListEntry>(`/waiting-list/${id}/status`, {
    status,
  });
}

export async function deleteWaitingListEntry(
  id: string
): Promise<ApiResponse<boolean>> {
  const response = await apiClient.delete<boolean>(`/waiting-list/${id}`);
  return {
    ...response,
    data: response.success,
  } as ApiResponse<boolean>;
}
